import createClient, { type Middleware } from 'openapi-fetch';
import i18n from '@/i18n';
import { isQueueable, queueAndRespond } from '@/offline/queue';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];

/** RFC-7807-Fehler der API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, problem: Record<string, unknown> | undefined) {
    super(String(problem?.detail ?? `HTTP ${status}`));
    this.status = status;
    this.code = problem?.code as string | undefined;
    this.details = problem ?? {};
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1];
}

let unauthorizedHandler: (() => void) | undefined;

/** Wird bei 401 (abgelaufene Sitzung) aufgerufen. */
export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

/** Sprache der Oberfläche für lokalisierte Server-Antworten (Fehlermeldungen, Exporte). */
function language(): string {
  return i18n.language?.startsWith('en') ? 'en' : 'de';
}

const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    request.headers.set('Accept-Language', language());
    if (!['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
      const token = readCookie('XSRF-TOKEN');
      if (token) {
        request.headers.set('X-XSRF-TOKEN', decodeURIComponent(token));
      }
    }
    return request;
  },
  onResponse({ response, request }) {
    if (response.status === 401 && !request.url.includes('/auth/')) {
      unauthorizedHandler?.();
    }
    return response;
  },
};

/** Typisierter API-Client, generiert aus docs/api/openapi.json (`npm run gen:api`). */
export const api = createClient<paths>({
  baseUrl: globalThis.location?.origin ?? '',
  credentials: 'same-origin',
  // fetch erst beim Aufruf auflösen (erlaubt Austausch in Tests); ohne Verbindung landen einfache
  // Zeiteintrags-Änderungen in der Offline-Warteschlange (PWA-03)
  fetch: async (request) => {
    const queueable = isQueueable(request.method, new URL(request.url).pathname);
    const copy = queueable ? request.clone() : null;
    try {
      return await globalThis.fetch(request);
    } catch (error) {
      if (copy && error instanceof TypeError) return queueAndRespond(copy);
      throw error;
    }
  },
});
api.use(csrfMiddleware);

/** Wandelt ein openapi-fetch-Ergebnis in Daten oder eine {@link ApiError} um. */
export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) {
    throw new ApiError(response.status, error as Record<string, unknown> | undefined);
  }
  return data as T;
}

/** Lädt eine Datei (z. B. CSV-Export) per POST und startet den Download. */
export async function downloadPost(path: string, body: unknown, fallbackName: string) {
  const token = readCookie('XSRF-TOKEN');
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': language(),
      ...(token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(response.status, await response.json().catch(() => undefined));
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match?.[1] ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Multipart-Upload einer Datei (z. B. Beleg); openapi-fetch serialisiert FormData nicht typisiert. */
export async function uploadFile<T>(path: string, file: File | Blob, filename?: string): Promise<T> {
  const token = readCookie('XSRF-TOKEN');
  const form = new FormData();
  form.append('file', file, filename ?? (file instanceof File ? file.name : 'upload'));
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Accept-Language': language(), ...(token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {}) },
    body: form,
  });
  const body = await response.json().catch(() => undefined);
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}
